import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { useReports } from '../hooks/useReports';
import { AuthModal } from '../components/auth/AuthModal';
import TreasureGrid from '../components/TreasureGrid';
import SimpleSearchInterface from '../components/SimpleSearchInterface';
import { FeatureFlagRealtimeDemo } from '../components/examples/FeatureFlagRealtimeDemo';
import { GamificationGate } from '../components/GamificationGate';
import ShareModal from '../components/ShareModal';
import { Report } from '../hooks/useReports';

// Category data for navigation
const categories = [
  { id: 'all', name: 'All', icon: '🏠', tag: '' },
  { id: 'furniture', name: 'Furniture', icon: '🪑', tag: 'furniture' },
  { id: 'electronics', name: 'Electronics', icon: '📱', tag: 'electronics' },
  { id: 'books', name: 'Books', icon: '📚', tag: 'books' },
  { id: 'clothing', name: 'Clothing', icon: '👕', tag: 'clothing' },
  { id: 'appliances', name: 'Appliances', icon: '🔌', tag: 'appliances' },
  { id: 'toys', name: 'Toys', icon: '🧸', tag: 'toys' },
  { id: 'garden', name: 'Garden', icon: '🌱', tag: 'garden' },
  { id: 'sports', name: 'Sports', icon: '⚽', tag: 'sports' },
  { id: 'art', name: 'Art', icon: '🎨', tag: 'art' },
  { id: 'tools', name: 'Tools', icon: '🔧', tag: 'tools' },
  { id: 'kitchen', name: 'Kitchen', icon: '🍳', tag: 'kitchen' }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useFirebase();
  const { reports, fetchReports, isLoading } = useReports();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [favoriteReportIds, setFavoriteReportIds] = useState<Set<string>>(new Set());
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareReport, setShareReport] = useState<Report | null>(null);

  // Load latest treasures on mount
  useEffect(() => {
    fetchReports({ 
      limit: 5,
      sortBy: 'submittedAt',
      sortOrder: 'desc'
    });
  }, [fetchReports]);

  const handleGetStarted = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      navigate('/treasures');
    }
  };

  const handleFavorite = (reportId: string, isFavorited: boolean) => {
    setFavoriteReportIds(prev => {
      const newSet = new Set(prev);
      if (isFavorited) {
        newSet.add(reportId);
      } else {
        newSet.delete(reportId);
      }
      return newSet;
    });
    
    // TODO: Implement actual favorite functionality with backend
    console.log(`${isFavorited ? 'Added' : 'Removed'} favorite for report ${reportId}`);
  };

  const handleShare = (reportId: string) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      setShareReport(report);
      setShareModalOpen(true);
    }
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setShareReport(null);
  };



  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      fetchReports({ 
        limit: 5,
        sortBy: 'submittedAt',
        sortOrder: 'desc'
      });
    } else {
      const category = categories.find(c => c.id === categoryId);
      if (category?.tag) {
        fetchReports({ 
          tags: [category.tag],
          limit: 5,
          sortBy: 'submittedAt',
          sortOrder: 'desc'
        });
      }
    }
  };



  return (
    <div className="min-h-screen">
      {/* Hero Section with Gradient Background - Mobile Optimized */}
      <div className="relative bg-gradient-to-br from-teddy via-sky-neutral to-teddy-200 safe-area-top">
        <div className="max-w-7xl mx-auto mobile-padding py-8 sm:py-12 md:py-16">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-display font-bold text-urban mb-4 sm:mb-6 leading-tight">
              Turn Trash into
              <span className="text-treasure block sm:inline"> Treasure</span>
            </h1>
            <p className="text-lg sm:text-xl text-urban-400 mb-8 sm:mb-12 max-w-3xl mx-auto px-2">
              Discover amazing curbside finds in your community. Share your discoveries, 
              help others find treasures, and promote sustainability through reuse.
            </p>
            
            {/* Simplified Search Interface */}
            <div className="mb-8 sm:mb-12">
              <SimpleSearchInterface className="max-w-4xl mx-auto" />
            </div>

            {/* User Status - Mobile Optimized */}
            {user && userProfile && (
              <div className="mb-6 sm:mb-8">
                <p className="text-base sm:text-lg text-urban mb-3 sm:mb-4 px-2">
                  Welcome back, <span className="font-semibold">{userProfile.displayName || user.displayName}</span>!
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 text-sm px-2">
                  <GamificationGate feature="coins">
                    <span className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full touch-target">
                      <span className="text-treasure">🪙</span>
                      <span className="font-medium text-urban">{userProfile.goldCoins} coins</span>
                    </span>
                  </GamificationGate>
                  <GamificationGate feature="coins">
                    <span className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full touch-target">
                      <span className="text-cardboard">🎫</span>
                      <span className="font-medium text-urban">{userProfile.farAwayTokens} tokens</span>
                    </span>
                  </GamificationGate>
                  <span className="capitalize bg-white/80 px-4 py-2 rounded-full font-medium text-urban touch-target">
                    {userProfile.subscriptionTier}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation - Mobile Optimized */}
      <div className="bg-white border-b border-urban-100 sticky top-16 sm:top-20 z-[1000]">
        <div className="max-w-7xl mx-auto mobile-padding">
          <div className="flex gap-4 sm:gap-8 overflow-x-auto scrollbar-hide py-3 sm:py-4 -mx-2 px-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className={`flex flex-col items-center gap-1 sm:gap-2 min-w-16 sm:min-w-20 py-2 sm:py-3 px-2 transition-all duration-200 border-b-2 touch-target ${
                  selectedCategory === category.id
                    ? 'border-cardboard text-urban'
                    : 'border-transparent text-urban-400 hover:text-urban hover:border-urban-200'
                }`}
                aria-label={`Filter by ${category.name}`}
              >
                <span className="text-xl sm:text-2xl">{category.icon}</span>
                <span className="text-xs font-medium whitespace-nowrap leading-tight">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Latest Treasures Grid - Mobile Optimized */}
      <div className="max-w-7xl mx-auto mobile-padding py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-3">
          <h2 className="text-xl sm:text-2xl font-display font-bold text-urban leading-tight">
            {selectedCategory === 'all' ? 'Latest Treasures' : `Latest ${categories.find(c => c.id === selectedCategory)?.name} Treasures`}
          </h2>
          <button
            onClick={() => navigate('/treasures')}
            className="text-cardboard hover:text-cardboard-600 font-medium text-sm sm:text-base touch-target self-start sm:self-auto"
            aria-label="View all treasures"
          >
            View all →
          </button>
        </div>

        {/* Treasure Cards Grid */}
        <TreasureGrid
          reports={reports.slice(0, 5)}
          isLoading={isLoading}
          onFavorite={handleFavorite}
          favoriteReportIds={favoriteReportIds}
          showCoins={true}
          onShare={handleShare}
          emptyStateTitle="No latest treasures found"
          emptyStateDescription={
            selectedCategory === 'all' 
              ? "No recent treasures have been shared yet. Be the first to discover and share a treasure with the community!"
              : `No recent ${categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} items found. Try a different category or check back later for new discoveries.`
          }
          emptyStateAction={{
            label: user ? 'Submit a Treasure' : 'Get Started',
            onClick: handleGetStarted
          }}
        />
      </div>

      {/* How It Works Section - Mobile Optimized */}
      <div className="bg-sky-neutral py-8 sm:py-12">
        <div className="max-w-7xl mx-auto mobile-padding">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-urban mb-3 sm:mb-4">
              How Curbside Quest Works
            </h2>
            <p className="text-base sm:text-lg text-urban-400 px-2">
              Join our community of treasure hunters and sustainability champions
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10">
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-cardboard-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">📸</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-urban">Discover & Share</h3>
              <p className="text-urban-400 text-sm sm:text-base leading-relaxed px-2">
                Spot something interesting on the curb? Take a photo and share it with the community.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-verge-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">🗺️</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-urban">Explore & Hunt</h3>
              <p className="text-urban-400 text-sm sm:text-base leading-relaxed px-2">
                Browse the map to find treasures near you. Filter by type, location, and pickup dates.
              </p>
            </div>

            <div className="text-center sm:col-span-2 md:col-span-1">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-treasure-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl">🏆</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-urban">Earn & Compete (Coming Soon...)</h3>
              <p className="text-urban-400">
                Earn coins and badges for your contributions. Climb the leaderboard and unlock rewards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Flag Demo - Development Only */}
      {import.meta.env.DEV && (
        <div className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto mobile-padding">
            <FeatureFlagRealtimeDemo />
          </div>
        </div>
      )}

      {/* CTA Section - Mobile Optimized */}
      <div className="bg-urban py-8 sm:py-12 safe-area-bottom">
        <div className="max-w-7xl mx-auto mobile-padding text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3 sm:mb-4 leading-tight">
            Ready to Start Your Treasure Hunt?
          </h2>
          <p className="text-lg sm:text-xl text-urban-100 mb-6 sm:mb-8 px-2">
            Join thousands of community members making a difference
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-treasure hover:bg-treasure-600 text-urban font-semibold px-8 py-4 rounded-xl transition-colors touch-target text-base sm:text-lg"
            aria-label={user ? 'Continue treasure hunting' : 'Get started with Curbside Quest'}
          >
            {user ? 'Continue Hunting' : 'Get Started Today'}
          </button>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signup"
      />

      {/* Share Modal */}
      {shareReport && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
          reportId={shareReport.id}
          reportTitle={shareReport.tags?.join(', ') || 'Curbside Treasure'}
          reportImage={shareReport.images?.[0]?.url}
          reportLocation={`${shareReport.location.suburb}, ${shareReport.location.postcode}`}
        />
      )}
    </div>
  );
};

export default HomePage;